import React from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import { SOCIAL_EXTERNAL_IDENTITY_PROVIDER_APPLE } from '@eb/social-login-facebook';

import AlertChunkySvg from 'eventbrite_design_system/iconography/icons/AlertChunky';
import NotificationBar from 'eventbrite_design_system/notification/NotificationBar';
import Button from 'eventbrite_design_system/button/Button';
import { SIZE_BLOCK } from 'eventbrite_design_system/button/constants';
import withMainControls from 'eventbrite_design_system/structure/hoc/withMainControls';
import { ICON_TYPE_PROP_TYPE } from 'eventbrite_design_system/icon/constants';
import { TYPE_ERROR } from 'eventbrite_design_system/notification/constants';

import PageHeader from '../../components/PageHeader';
import LookUpForm from '../../components/forms/LookUpForm';
import { EXISTING_ACCOUNT_INFO } from '../../constants';
import { APPLE_AUTHENTICATION_DATA_PROPTYPES } from '../../constants/proptypes';

class ExistingAccountScreen extends React.Component {
    static propTypes = {
        socialAuthenticationData:
            APPLE_AUTHENTICATION_DATA_PROPTYPES.isRequired,

        authNotificationOptions: PropTypes.shape({
            message: PropTypes.node,
            type: PropTypes.string,
            iconType: ICON_TYPE_PROP_TYPE,
        }),
        trackOnLoadPage: PropTypes.func,
        trackOnYesButton: PropTypes.func,
    };

    /**
     * Create account as it is non existent + login
     * Possible errors:
     * * Token > expired due to expirationTime
     * * * Show error `expired token` > Go back to step 1
     */

    componentDidMount() {
        const {
            trackOnLoadPage,
            socialAuthenticationData: { isEmailHidden },
        } = this.props;

        if (trackOnLoadPage) {
            trackOnLoadPage(isEmailHidden);
        }
    }

    _handleExistingUserClick = () => {
        const { trackOnYesButton, onExistingAccountCheck } = this.props;

        onExistingAccountCheck({ userHasAccount: true });

        if (trackOnYesButton) {
            trackOnYesButton();
        }
    };

    _handleOnNewUserClick = () => {
        const {
            socialAuthenticationData: { idToken },
            linkAccountOnNewUser,
            onExistingAccountCheck,
        } = this.props;

        onExistingAccountCheck({ userHasAccount: false });

        linkAccountOnNewUser({
            externalProvider: SOCIAL_EXTERNAL_IDENTITY_PROVIDER_APPLE,
            externalToken: idToken,
        });
    };

    _renderAuthNotification = () => {
        const {
            authNotificationOptions,
            shouldRenderAuthNotification,
        } = this.props;

        let component = null;

        if (shouldRenderAuthNotification) {
            const { message, ...props } = authNotificationOptions;

            component = (
                <div className="eds-l-mar-top-4 eds-l-mar-bot-8 sign-in-screen__notification-container">
                    <NotificationBar {...props}>{message}</NotificationBar>
                </div>
            );
        }

        return component;
    };

    _handleSubmit = (formData) => {
        const { clearAuthNotification, onAccountStatusCheck } = this.props;

        // clear notification everytime form is submitted
        if (clearAuthNotification) {
            clearAuthNotification();
        }

        return onAccountStatusCheck(formData);
    };

    _handleClearNotification() {
        const { hideMainNotification } = this.props;

        this._hasNotification = false;
        hideMainNotification();
    }

    _handleShowNotification(error) {
        const { addMainNotification } = this.props;

        /*Limit the call to addMainNotification to only if
        a notification hasn't been shown since last submit.*/
        if (!this._hasNotification) {
            addMainNotification({
                type: TYPE_ERROR,
                children: error,
                iconType: <AlertChunkySvg />,
            });

            this._hasNotification = true;
        }
    }

    render() {
        const { userHasAccount } = this.props;

        const gridClasses = classNames(
            'eds-g-cell',
            'eds-g-cell-sm-8-12 eds-g-offset-sm-2-12',
            'eds-g-cell-sw-6-12 eds-g-offset-sw-3-12',
            'eds-g-cell-md-4-12 eds-g-offset-md-4-12',
            'eds-g-cell-10-12 eds-g-offset-1-12',
        );

        let component = (
            <div>
                <div
                    className="eds-l-mar-bot-4"
                    data-automation="social-login-existing-account"
                >
                    <Button
                        size={SIZE_BLOCK}
                        style="fill"
                        onClick={this._handleExistingUserClick}
                    >
                        {EXISTING_ACCOUNT_INFO.yesButtonText}
                    </Button>
                </div>
                <div
                    className="eds-l-mar-bot-4"
                    data-automation="social-login-new-account"
                >
                    <Button
                        size={SIZE_BLOCK}
                        style="neutral"
                        onClick={this._handleOnNewUserClick}
                    >
                        {EXISTING_ACCOUNT_INFO.noButtonText}
                    </Button>
                </div>
            </div>
        );

        if (userHasAccount) {
            component = (
                <LookUpForm
                    buttonCTA="Next"
                    shouldAnimate={true}
                    onSubmit={this._handleSubmit}
                    hideSocialLogin={true}
                    showNotification={this._handleShowNotification.bind(this)}
                    clearNotification={this._handleClearNotification.bind(this)}
                />
            );
        }

        return (
            <div className="eds-g-group">
                <div className={gridClasses}>
                    <PageHeader
                        headerText={EXISTING_ACCOUNT_INFO.headerText}
                        subtitleText={EXISTING_ACCOUNT_INFO.subtitleText}
                    />

                    {this._renderAuthNotification()}

                    {component}
                </div>
            </div>
        );
    }
}

export default withMainControls(ExistingAccountScreen);
